import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Input, Button, Spin, Tag } from 'antd'
import styled from 'styled-components'
import { getReport } from './aiInterviewAPI'
import {
  communicationMetrics,
  communicationVerdict,
  formatScore,
  isCommunicationParameter,
} from './communicationAnalytics'
import { VERDICT_COLORS, effectiveVerdict } from './aiInterviewVerdict'

const Page = styled.div`padding:24px 32px;max-width:920px;margin:0 auto;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;`
const H1 = styled.h1`font-size:22px;font-weight:600;color:#0f172a;margin-bottom:4px;`
const Sub = styled.div`color:#64748b;font-size:13px;margin-bottom:20px;`
const Card = styled.section`background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:18px 22px;margin-bottom:14px;`
const Label = styled.div`font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#6366f1;font-weight:600;margin-bottom:6px;`
const Quote = styled.div`font-style:italic;color:#475569;border-left:3px solid #6366f1;padding:4px 0 4px 12px;margin:6px 0 8px;font-size:14px;`
const ParamRow = styled.div`display:grid;grid-template-columns:2fr 80px 3fr;gap:12px;align-items:center;padding:10px 0;border-bottom:1px solid #f1f5f9;&:last-child{border-bottom:none;}`
const Stars = styled.span`color:#f59e0b;font-size:14px;letter-spacing:1px;`
const Verdict = styled.span`
  display:inline-block;padding:6px 14px;border-radius:8px;font-weight:600;
  background: ${(p) => (VERDICT_COLORS[p.verdict] || VERDICT_COLORS['Not attempted']).bg};
  color: ${(p) => (VERDICT_COLORS[p.verdict] || VERDICT_COLORS['Not attempted']).fg};
`

// Mid-interview language change (Hinglish interviews). Informational, not a
// red flag — the candidate asked to continue in a language they could follow,
// and it is deliberately excluded from scoring. Styled neutral-amber so it
// reads as context rather than as a proctoring violation.
const LanguageNote = styled(Card)`border-left:4px solid #f59e0b;background:#fffbeb;`

// Speech delivery block — how the candidate spoke, not what they said.
// Report-only: never part of the fit score, and the note in the card says so
// on screen so a reviewer doesn't double-count it against the parameters.
const SpeechGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px;margin-bottom:10px;`
const SpeechTile = styled.div`border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;background:#f8fafc;`
const TileLabel = styled.div`font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#6b7280;font-weight:600;margin-bottom:6px;`
const TileScore = styled.div`font-size:26px;font-weight:700;color:#0f172a;line-height:1.1;`
const TileUnit = styled.span`font-size:13px;font-weight:600;color:#64748b;`
const TileBand = styled.div`font-size:13px;font-weight:600;color:#6366f1;margin-top:2px;`
const TileDetail = styled.div`font-size:12px;color:#64748b;margin-top:8px;line-height:1.5;`
const SpeechNote = styled.div`font-size:12px;color:#64748b;background:#f8fafc;border-left:3px solid #94a3b8;padding:10px 14px;border-radius:0 6px 6px 0;line-height:1.6;margin-top:10px;`
const UnclearWord = styled(Tag)`margin:2px 4px 2px 0;`

const stars = (n) => '★'.repeat(n) + '☆'.repeat(5 - n)

const AIInterviewReport = () => {
  const { sessionId: paramId } = useParams()
  const [sessionId, setSessionId] = useState(paramId || '')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = async (id) => {
    if (!id) return
    setLoading(true); setError(null)
    try {
      const resp = await getReport(id)
      setData(resp?.data?.data || resp?.data)
    } catch (e) {
      console.error(e); setError(e?.response?.data?.message || 'Failed to load')
    } finally { setLoading(false) }
  }

  useEffect(() => { if (paramId) load(paramId) }, [paramId])

  const score = data?.score

  return (
    <Page>
      <H1>AI Interview Report</H1>
      <Sub>Narrative analysis with quote-backed evidence and a hiring recommendation.</Sub>

      <Card>
        <Label>Session ID</Label>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input value={sessionId} onChange={(e) => setSessionId(e.target.value)} placeholder="Paste a session id…" />
          <Button type="primary" onClick={() => load(sessionId)} loading={loading}>Load</Button>
        </div>
      </Card>

      {error && (
        <Card><div style={{ color: '#dc2626' }}>{error}</div></Card>
      )}

      {loading && <Card><Spin /></Card>}

      {!loading && data && (
        <>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Label>Status</Label>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{data.status}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  {data.totalDuration ? `${data.totalDuration}s` : '—'} · {(data.transcript || []).length} turns
                </div>
              </div>
              {score && (() => {
                const v = effectiveVerdict(score.overallScore, score.verdict)
                return (
                  <div style={{ textAlign: 'right' }}>
                    <Verdict verdict={v}>{v}</Verdict>
                    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
                      {Math.round(score.overallScore || 0)}<span style={{ fontSize: 14, color: '#64748b' }}>/100</span>
                    </div>
                  </div>
                )
              })()}
            </div>
          </Card>

          {data.languageSwitch && (
            <LanguageNote>
              <Label>Language barrier</Label>
              <div style={{ fontSize: 15, color: '#334155' }}>
                The candidate asked to continue in <strong>{data.languageSwitch.to}</strong> instead of{' '}
                <strong>{data.languageSwitch.from}</strong>. Every question from that point on was asked in{' '}
                {data.languageSwitch.to}. This did not affect their score.
              </div>
              {data.languageSwitch.evidence && (
                <Quote>“{data.languageSwitch.evidence}”</Quote>
              )}
            </LanguageNote>
          )}

          {score?.executiveSummary && (
            <Card>
              <Label>Executive Summary</Label>
              <div style={{ fontSize: 15, lineHeight: 1.65, color: '#334155' }}>
                {score.executiveSummary}
              </div>
            </Card>
          )}

          {data.speechProfile && (
            <Card>
              <Label>Speech delivery</Label>
              <SpeechGrid>
                {data.speechProfile.pronunciationScore != null && (
                  <SpeechTile>
                    <TileLabel>Speech clarity</TileLabel>
                    <TileScore>
                      {Math.round(data.speechProfile.pronunciationScore)}<TileUnit>/100</TileUnit>
                    </TileScore>
                    <TileBand>{data.speechProfile.pronunciationBand}</TileBand>
                    {data.speechProfile.totalLowConfidenceCount != null && (
                      <TileDetail>
                        {data.speechProfile.totalLowConfidenceCount} of {data.speechProfile.totalWords} words
                        were hard for the speech recognizer to make out.
                      </TileDetail>
                    )}
                  </SpeechTile>
                )}
                {data.speechProfile.fluencyScore != null && (
                  <SpeechTile>
                    <TileLabel>Fluency</TileLabel>
                    <TileScore>
                      {Math.round(data.speechProfile.fluencyScore)}<TileUnit>/100</TileUnit>
                    </TileScore>
                    <TileBand>{data.speechProfile.fluencyBand}</TileBand>
                    <TileDetail>
                      {Math.round(data.speechProfile.wpm)} words/min
                      {data.speechProfile.totalFillerCount != null &&
                        ` · ${data.speechProfile.totalFillerCount} filler words`}
                      {data.speechProfile.pauseRatePer100 != null &&
                        ` · ${data.speechProfile.pauseRatePer100} pauses per 100 words`}
                    </TileDetail>
                  </SpeechTile>
                )}
              </SpeechGrid>

              {Array.isArray(data.speechProfile.unclearWords) && data.speechProfile.unclearWords.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <TileLabel>Least clearly recognised words</TileLabel>
                  {data.speechProfile.unclearWords.map((w) => (
                    <UnclearWord key={w.word} color="orange">
                      {w.word} · {Math.round((w.confidence || 0) * 100)}%
                    </UnclearWord>
                  ))}
                </div>
              )}

              <SpeechNote>
                <strong>How to read this:</strong> these describe how the candidate spoke, not what they
                said, and are <strong>not part of the fit score</strong>. Clarity is derived from
                speech-recognition confidence, which is also affected by microphone quality, background
                noise and uncommon technical words — an intelligibility signal, not a judgement of accent.
                {data.speechProfile.isCalibratedLanguage === false && (
                  <> Recognition confidence is calibrated per language model, so for this{' '}
                    non-English interview the number is indicative and not comparable to an English one.</>
                )}
                {data.speechProfile.coverage === 'partial' && (
                  <> Measured on {data.speechProfile.turnsMeasured} of {data.speechProfile.turnsAnswered}{' '}
                    answered questions — the rest were too short or had no speech telemetry.</>
                )}
              </SpeechNote>
            </Card>
          )}

          {Array.isArray(score?.strengths) && score.strengths.length > 0 && (
            <Card>
              <Label>What they're strong at</Label>
              {score.strengths.map((s, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600 }}>✓ {s.claim || s.summary || s}</div>
                  {s.quote && <Quote>"{s.quote}"</Quote>}
                </div>
              ))}
            </Card>
          )}

          {Array.isArray(score?.concerns) && score.concerns.length > 0 && (
            <Card>
              <Label>What's lacking / worth probing</Label>
              {score.concerns.map((c, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600 }}>⚠ {c.claim || c.summary || c}</div>
                  {c.quote && <Quote style={{ borderLeftColor: '#f59e0b' }}>"{c.quote}"</Quote>}
                  {c.impact && <div style={{ fontSize: 12, color: '#64748b' }}>Impact: {c.impact}</div>}
                </div>
              ))}
            </Card>
          )}

          {Array.isArray(score?.parameterScores) && score.parameterScores.length > 0 && (
            <Card>
              <Label>Per-parameter detail</Label>
              {score.parameterScores.map((p, i) => (
                <ParamRow key={i}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {isCommunicationParameter(p) ? communicationVerdict(p) : p.rating_label}
                    </div>
                    {isCommunicationParameter(p) && (
                      <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 700, marginTop: 4 }}>
                        {formatScore(p.score)}
                      </div>
                    )}
                  </div>
                  <Stars>{stars(p.rating || 0)}</Stars>
                  <div>
                    <div style={{ fontSize: 13 }}>{p.analysis}</div>
                    {communicationMetrics(p).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 8 }}>
                        {communicationMetrics(p).map((metric) => (
                          <span key={metric.label} style={{ fontSize: 12, color: '#64748b' }}>
                            {metric.label}: <strong style={{ color: '#334155' }}>{metric.value}</strong>
                          </span>
                        ))}
                      </div>
                    )}
                    {p.supporting_quote && <div style={{ fontStyle: 'italic', color: '#64748b', fontSize: 12, marginTop: 4 }}>"{p.supporting_quote}"</div>}
                  </div>
                </ParamRow>
              ))}
            </Card>
          )}

          {score?.scoreRationale && (
            <Card style={{ borderLeft: '4px solid #6366f1', background: '#f8f7ff' }}>
              <Label>Why this score</Label>
              <div style={{ fontSize: 14, lineHeight: 1.6, color: '#334155', fontWeight: 500 }}>
                {score.scoreRationale}
              </div>
            </Card>
          )}

          {score?.recommendationText && (
            <Card>
              <Label>Recommendation</Label>
              <div style={{ fontSize: 14, lineHeight: 1.6, color: '#334155' }}>{score.recommendationText}</div>
            </Card>
          )}

          <Card>
            <Label>Full transcript</Label>
            {(data.transcript || []).map((t, i) => (
              <div key={t.interactionId || i} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Tag>{t.questionType}</Tag>
                  {t.isFollowUp && <Tag color="purple">follow-up</Tag>}
                </div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>Q: {t.questionText}</div>
                <div style={{ marginTop: 6, color: '#334155' }}>A: {t.candidateResponse || <span style={{ color: '#94a3b8' }}>(no answer)</span>}</div>
                {t.reasoning && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>AI reasoning: {t.reasoning}</div>}
              </div>
            ))}
          </Card>
        </>
      )}
    </Page>
  )
}

export default AIInterviewReport
